import React, { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import { universityApi } from '../api/university'
import { useNoKeyboardResize } from '../hooks/useNoKeyboardResize'

const ERROR_COLOR = 'rgba(180, 5, 8, 0.8)' // #B40508 80%

/**
 * 학교 메일 입력.
 *
 * 회원가입의 `PhoneSignup` 과 같은 모양이다(밑줄 입력 + 아래 한 줄 안내 +
 * 하단 버튼). 오류를 띄우는 때도 같다 — **버튼을 눌렀을 때만.** 치는 도중에
 * 틀렸다고 하면 `admin@tas` 까지 쳤을 뿐인 사람에게 잔소리가 된다.
 * 초록 체크는 반대로 맞는 순간 바로 띄운다. 맞다고 알려주는 건 방해가 아니다.
 *
 * 서버도 같은 검사를 다시 한다. 여기 검사는 사용자를 돕기 위한 것이지
 * 막는 장치가 아니다.
 */
const UniversityEmail: React.FC = () => {
  const navigate = useNavigate()
  useNoKeyboardResize()

  const [params] = useSearchParams()
  const universityId = Number(params.get('id') || 0)
  const universityName = params.get('name') || ''
  const domain = params.get('domain') || ''

  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)

  /**
   * 고른 학교 메일인지.
   *
   * 딱 맞는 것(`@dgu.ac.kr`)과 하위 도메인(`@gs.dgu.ac.kr`)을 받는다. 대학원·
   * 단과대가 하위 도메인을 쓰는 곳이 흔하다. 점을 붙여 비교해야 `@notdgu.ac.kr`
   * 처럼 뒤만 같은 주소가 통과하지 않는다. 서버의 `matchesDomain` 과 같은 규칙이다.
   */
  const isSchoolEmail = useMemo(() => {
    const v = email.trim().toLowerCase()
    const at = v.lastIndexOf('@')
    if (at <= 0 || !domain) return false
    const local = v.slice(0, at)
    const host = v.slice(at + 1)
    if (!local) return false
    const d = domain.toLowerCase()
    return host === d || host.endsWith('.' + d)
  }, [email, domain])

  // 입력하는 "동안"은 아무 말도 하지 않는다. `admin@tas` 까지 쳤을 뿐인데
  // 틀렸다고 하면 안 된다. 회원가입(`PhoneSignup`)과 같게 **버튼을 눌렀을 때**만
  // 빨간 줄을 띄운다. 초록 체크는 반대다 — 맞는 순간 바로 보여줘도 잔소리가 아니다.
  const showError = Boolean(error)
  const message = error

  const handleSend = async () => {
    if (!isSchoolEmail || sending) return
    setSending(true)
    setError('')
    try {
      await universityApi.sendCode(universityId, email.trim().toLowerCase())
      navigate(
        `/university/verify?id=${universityId}` +
          `&name=${encodeURIComponent(universityName)}` +
          `&email=${encodeURIComponent(email.trim().toLowerCase())}`
      )
    } catch (e: any) {
      setError(e?.response?.data?.message || '인증 메일을 보내지 못했습니다')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        <AppBar onBack={() => navigate(-1)} />

        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pt-40px">
          <p className="font-pretendard font-bold text-22px leading-135% text-brown-80%-main">
            학교 이메일을 입력해주세요
          </p>

          <div className="mt-32px">
            <input
              value={email}
              onChange={(e) => {
                setEmail(e.target.value.trim())
                if (error) setError('')
              }}
              inputMode="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={domain ? `name@${domain}` : 'name@univ.ac.kr'}
              className="w-full bg-transparent border-none outline-none font-pretendard font-bold text-22px leading-135% placeholder:text-472410-20% py-8px"
              style={{ color: showError ? ERROR_COLOR : 'rgba(75, 54, 33, 0.8)' }}
            />
            <div
              className="h-2px w-full"
              style={{
                backgroundColor: showError ? ERROR_COLOR : 'rgba(75, 54, 33, 0.2)',
              }}
            />

            {showError ? (
              <div className="flex items-center gap-8px mt-12px">
                <img src="/images/signup-x.png" alt="" className="w-20px h-20px object-contain" />
                <span
                  className="font-pretendard font-bold text-15px"
                  style={{ color: ERROR_COLOR }}
                >
                  {message}
                </span>
              </div>
            ) : (
              isSchoolEmail && (
                <div className="flex items-center gap-8px mt-12px">
                  <img
                    src="/images/signup-check.png"
                    alt=""
                    className="w-20px h-20px object-contain"
                  />
                  <span className="font-pretendard font-regular text-15px text-textColorText3">
                    사용할 수 있는 이메일입니다
                  </span>
                </div>
              )
            )}
          </div>
        </div>

        <div className="w-full px-20px">
          <SubmitButton
            text="인증 코드 받기"
            disabled={!isSchoolEmail}
            loading={sending}
            onSubmit={handleSend}
            onDisabled={() =>
              setError(
                email.trim() ? '학교 이메일만 사용할 수 있어요' : '학교 이메일을 입력해주세요'
              )
            }
          />
        </div>
      </div>
    </div>
  )
}

export default UniversityEmail
